<template>
  <!-- One team's half of the Home card. The two halves face each other across
       the card, home mirrored on the right, each with its own pick button
       under it -- so the line you are picking sits under the team it belongs
       to, rather than at the end of a row. -->
  <!-- h-full + mt-auto on the button: the two halves are grid cells of equal
       height, so a QB flag on one side does not push only that side's button
       down -- the two lines always sit level. -->
  <div class="flex h-full min-w-0 flex-col gap-2" :class="isHome ? 'items-end' : 'items-start'">
    <!-- items-start, not centre: a QB flag adds a line, and centring would drop
         that side's logo below the other one and the card's "@". -->
    <div class="flex min-w-0 max-w-full items-start gap-2" :class="isHome ? 'flex-row-reverse' : ''">
      <img
        v-if="logoSrc && !broken"
        :src="logoSrc"
        :alt="team.abbr"
        class="h-10 w-10 shrink-0 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.45)]"
        loading="lazy"
        @error="broken = true"
      />
      <span v-else class="flex h-10 w-10 shrink-0 items-center justify-center text-xs font-bold" :style="{ color: accent }">
        {{ team.abbr }}
      </span>

      <div class="min-w-0" :class="isHome ? 'text-right' : ''">
        <div class="flex min-w-0 items-baseline gap-1.5" :class="isHome ? 'flex-row-reverse' : ''">
          <span class="shrink-0 text-[17px] font-extrabold leading-tight text-white">{{ team.abbr }}</span>
          <span class="min-w-0 truncate text-xs font-medium text-slate-200">{{ team.shortName || team.name }}</span>
          <!-- The card's "@" says which side is home; this keeps it for screen readers. -->
          <span v-if="isHome" class="sr-only">Home team</span>
        </div>
        <!-- null is "unknown" and prints nothing; a team with no games yet
             arrives as a real "0-0" from the server. Never fall back to one. -->
        <p v-if="team.record || team.atsRecord || qbFlag" class="mt-0.5 whitespace-nowrap text-[11px] tabular-nums text-slate-300">
          <span v-if="team.record">{{ team.record }}</span>
          <span v-if="team.record && team.atsRecord" class="mx-1 text-slate-500">·</span>
          <span v-if="team.atsRecord" title="Against the pool's frozen line">{{ team.atsRecord }} ATS</span>
        </p>
        <!-- The starting QB's injury status: context for a market move, never
             an input to the edge. On its own line here because half a card is
             too narrow to share one with the record. A tap swaps the label for
             the name and status, since phones have no hover. -->
        <button
          v-if="qbFlag"
          type="button"
          class="mt-0.5 text-[11px] font-semibold focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-slate-300"
          :class="qbFlag.tone"
          :title="qbFlag.title"
          :aria-label="`Starting quarterback ${qbFlag.title}`"
          :aria-expanded="showQbName"
          @click.stop="showQbName = !showQbName"
        ><span aria-hidden="true">⚠ {{ showQbName ? qbFlag.title : qbFlag.label }}</span></button>
      </div>
    </div>

    <!-- A pick uses the pool's frozen line, from this team's perspective.
         White, never green: yellow and lime mean line-value strength, so a
         green pick button would read as a rating. -->
    <button
      v-if="pickable"
      type="button"
      class="mt-auto flex h-11 w-full items-center justify-between gap-2 rounded-[10px] border px-3 text-base font-extrabold tabular-nums transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      :class="picked
        ? 'border-white bg-white/20 text-white'
        : 'border-white/30 bg-brand-bg/40 text-white hover:border-white/60 hover:bg-white/[0.06]'"
      :aria-label="`${picked ? 'Remove' : 'Pick'} ${team.abbr}, ${frozenSpread == null ? 'pool line pending' : 'frozen spread ' + pickLine}`"
      :title="frozenSpread == null ? 'Pool line pending' : 'Frozen pool spread'"
      :aria-pressed="picked"
      @click="$emit('pick')"
    >
      <span>{{ pickLine }}</span>
      <span aria-hidden="true" class="text-lg" :class="picked ? 'font-extrabold text-white' : 'font-normal text-slate-300'">{{ picked ? '✓' : '+' }}</span>
    </button>
  </div>
</template>

<script>
import { formatSigned } from "../utils/edge.js";
import { qbInjuryFlag } from "../utils/qbInjury.js";
import { teamAccent } from "../utils/teamColors.js";
import { teamLogo } from "../utils/teamLogo.js";

export default {
  name: "TeamSide",
  props: {
    team: { type: Object, required: true },
    isHome: { type: Boolean, default: false },
    pickable: { type: Boolean, default: false },
    picked: { type: Boolean, default: false },
    // Stored from the home team's perspective; never use the market here.
    frozenSpread: { type: Number, default: null },
  },
  emits: ["pick"],
  data() {
    return { showQbName: false, broken: false };
  },
  computed: {
    qbFlag() {
      return qbInjuryFlag(this.team.qbInjury);
    },
    pickLine() {
      if (this.frozenSpread == null) return "—";
      return formatSigned(this.isHome ? this.frozenSpread : -this.frozenSpread);
    },
    logoSrc() {
      return this.team.logo || teamLogo(this.team.abbr);
    },
    accent() {
      return teamAccent(this.team.name || this.team.abbr);
    },
  },
};
</script>
